import { AccordionDisclosure } from "theme";
import { EffectCarousel } from "theme";

class AccordionDisclosureGroup extends HTMLElement {
  constructor() {
    super();
    this.accordions = this.querySelectorAll('details');
  }
  connectedCallback() {
    this.accordions.forEach((details) => {
      details.addEventListener("click", this.closeOthers.bind(this), true);
    });
  }
  disconnectedCallback() {
    if (this.accordions) {
      this.accordions.forEach((details) => {
        details.removeEventListener("click", this.closeOthers.bind(this), true);
      });
    }
  }
  closeOthers(e) {
    const clickedDetails = e.target.closest('details');
    this.accordions.forEach((details) => {
      if (details !== clickedDetails) {
        details.removeAttribute('open');
        details.setAttribute('aria-expanded', 'false');
      }
    });
  }
}
customElements.define("accordion-group", AccordionDisclosureGroup);


/* -------------------------------------------
 * <surface-selector>
 * ----------------------------------------- */

class SurfaceSelector extends HTMLElement {
  constructor() {
    super();
    this._onChange = this._onChange.bind(this);
    this._onVariantChange = this._onVariantChange.bind(this);
    const script = this.querySelector("script[data-variant-recommendation]");
    let variants = [];
    if (script && script.textContent) {
      try {
        const parsed = JSON.parse(script.textContent);
        variants = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error("Invalid JSON in data-variant-recommendation script:", e);
        variants = [];
      }
    }
    this._variantAvailable = Array.isArray(variants) ? variants : [];
  }

  connectedCallback() {
    this._attachListeners();

    this._onOpenButtonClick = () => {
      const variantId = new URLSearchParams(window.location.search).get('variant');
      if (variantId) {
        this._initFromVariant(variantId);
      } else {
        const checkedInput = this.closest('quick-order-list')?.querySelector('input[type="radio"]:checked');
        if (checkedInput && (checkedInput.dataset.variantId || checkedInput.value)) {
          this._initFromVariant(checkedInput.dataset.variantId || checkedInput.value);
        }
      }
    };

    const openButton = document.querySelector('[aria-controls="paint-calculator"]');
    if (openButton) {
      openButton.addEventListener('click', this._onOpenButtonClick);
    }
  }

  disconnectedCallback() {
    this._detachListeners();
  }

  _attachListeners() {
    this._selects = this.querySelectorAll('select');
    this._selects.forEach(select => {
      select.addEventListener('change', this._onChange);
    });
    document.addEventListener('variant:change', this._onVariantChange);
  }

  _detachListeners() {
    if (this._selects) {
      this._selects.forEach(select => {
        select.removeEventListener('change', this._onChange);
      });
    }
    document.removeEventListener('variant:change', this._onVariantChange);
    const openButton = document.querySelector('[aria-controls="paint-calculator"]');
    if (openButton && this._onOpenButtonClick) {
      openButton.removeEventListener('click', this._onOpenButtonClick);
    }
  }

  _initFromVariant(variantId) {
    const variant = this._variantAvailable.find(v => String(v.id) === String(variantId));
    if (variant) {
      const surfaceVal = Array.isArray(variant.surface) ? variant.surface[0] : variant.surface;
      const sheenVal = Array.isArray(variant.sheen) ? variant.sheen[0] : variant.sheen;

      const availableSheens = Array.from(
        new Set(
          this._variantAvailable
            .filter(v => Array.isArray(v.surface) && v.surface.includes(surfaceVal))
            .flatMap(v => Array.isArray(v.sheen) ? v.sheen : [])
            .filter(sheen => sheen !== undefined && sheen !== null && sheen !== '')
        )
      );

      this._selects.forEach(select => {
        const isSurface = select.name.toLowerCase().includes('surface');
        const isSheen = select.name.toLowerCase().includes('sheen');

        if (isSurface) {
          select.value = surfaceVal;
        } else if (isSheen) {
          Array.from(select.options).forEach(option => {
            if (option.value !== '') {
              option.disabled = !availableSheens.includes(option.value);
            }
          });
          select.value = sheenVal;
        }
      });

      const sizeAccordion = document.querySelector('.accordion--size');
      if (sizeAccordion) {
        sizeAccordion.removeAttribute('disabled');
        if (typeof sizeAccordion.open === 'function') {
          sizeAccordion.open();
        }
        sizeAccordion.setAttribute('open', '');
        sizeAccordion.setAttribute('aria-expanded', 'true');
      }
      this._updateFinishDisplay(variant);
    }
  }

  _updateFinishDisplay(variant) {
    const finishSelectedOption = document.querySelector('.accordion--finish selected-option');
    if (finishSelectedOption) {
      const span = finishSelectedOption.querySelector('span');
      if (span) {
        const surfaceVal = Array.isArray(variant.surface) ? variant.surface[0] : variant.surface;
        const sheenVal = Array.isArray(variant.sheen) ? variant.sheen[0] : variant.sheen;
        span.textContent = `${surfaceVal}${sheenVal ? ' - ' + sheenVal : ''}`;
        const check = finishSelectedOption.querySelector('input[type="checkbox"]');
        if (check) check.checked = true;
      }
    }
  }

  _onVariantChange(event) {
    if (event.detail?.variant) return;

    this._selects.forEach(select => {
      select.value = '';
      select.removeAttribute('disabled');
      Array.from(select.options).forEach(option => {
        option.disabled = false;
      });
    });
  }

  _onChange(event) {
    const selectedSurface = event.target.value;
    const availableSheens = Array.from(
      new Set(
        this._variantAvailable
          .filter(variant => Array.isArray(variant.surface) && variant.surface.includes(selectedSurface))
          .flatMap(variant => Array.isArray(variant.sheen) ? variant.sheen : [])
          .filter(sheen => sheen !== undefined && sheen !== null && sheen !== '')
      )
    );

    this._selects.forEach(select => {
      if (select !== event.target) {
        select.removeAttribute('disabled');
        Array.from(select.options).forEach(option => {
          if (option.value === '') {
            option.disabled = false;
          } else if (select.name.toLowerCase().includes('sheen')) {
            option.disabled = !availableSheens.includes(option.value);
          } else {
            option.disabled = false;
          }
          if (option.selected && option.disabled) {
            select.value = '';
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    });

    const selectedValues = Array.from(this._selects)
      .map(select => select.value)
      .filter(value => value !== '');

    if (selectedValues.length === this._selects.length) {
      const recommendedVariants = this._variantAvailable.filter(variant => {
        return selectedValues.every(value =>
          (Array.isArray(variant.surface) && variant.surface.includes(value)) ||
          (Array.isArray(variant.sheen) && variant.sheen.includes(value))
        );
      });
      for (const variant of recommendedVariants) {
        const valueToMatch = String(variant.id);
        const labels = this.closest('quick-order-list')?.querySelectorAll('label[data-variant-id]');
        if (!labels) continue;
        for (const label of labels) {
          const variantIds = label.dataset.variantId.split(',').map(id => id.trim());
          if (variantIds.includes(valueToMatch)) {
            label.classList.add('selected');
            const input = label.parentElement.querySelector('input[type="radio"], input[type="checkbox"]');
            if (input) {
              input.dataset.triggeredBy = 'surface-selector';
              setTimeout(() => {
                input.click();
                input.checked = true;
              }, 200);
            }
            return;
          } else {
            label.classList.remove('selected');
          }
        }
      }
    } else {
      const labels = this.closest('quick-order-list')?.querySelectorAll('label[data-variant-id]');
      if (labels) {
        labels.forEach(label => {
          const input = label.parentElement.querySelector('input[type="radio"], input[type="checkbox"]');
          label.classList.remove('selected');
          if (input) {
            input.checked = false;
            const sizeAccordion = document.querySelector('.accordion--size');
            if (sizeAccordion) {
              if (typeof sizeAccordion.close === 'function') {
                sizeAccordion.close();
              }
              sizeAccordion.removeAttribute('open');
              sizeAccordion.setAttribute('aria-expanded', 'false');
              sizeAccordion.setAttribute('disabled', '');
            }
            const selectedOption = document.querySelector('selected-option');
            if (selectedOption) {
              const span = selectedOption.querySelector('span');
              const input = selectedOption.querySelector('input');
              if (span) span.innerHTML = 'Select finish';
              if (input) input.checked = false;
            }
          }
        });
      }
    }

    this.dispatchEvent(new CustomEvent('surface-selector:change', {
      detail: { name: event.target.name, value: event.target.value },
      bubbles: true
    }));
  }
}

customElements.define('surface-selector', SurfaceSelector);


/* -------------------------------------------
 * <quick-order-list-submit>
 * ----------------------------------------- */

class QuickOrderListSubmit extends HTMLElement {
  static get observedAttributes() {
    return ['include-zero'];
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.setAttribute('role', this.getAttribute('role') || 'button');
    this.tabIndex ||= 0;
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.click();
      }
    });

    this._bindQuantityChange();
    this._updateTotalPrice();

    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this._unbindQuantityChange();
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  _bindQuantityChange() {
    const root = this.closest('quick-order-list') || document.querySelector('quick-order-list') || document;
    this._quantityInputs = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id] input[type="number"], quick-order-list-quantity-selector[variant-id] input[inputmode="numeric"]')
    );
    this._quantityInputs.forEach(input => {
      input.addEventListener('input', this._updateTotalPrice.bind(this));
      input.addEventListener('change', this._updateTotalPrice.bind(this));
    });
  }

  _unbindQuantityChange() {
    if (this._quantityInputs) {
      this._quantityInputs.forEach(input => {
        input.removeEventListener('input', this._updateTotalPrice);
        input.removeEventListener('change', this._updateTotalPrice);
      });
    }
  }

  _updateTotalPrice = () => {
    const root = document;
    const selectors = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id]')
    );
    let total = 0;
    selectors.forEach(el => {
      const input =
        el.querySelector('.quantity-selector__input-wrapper input') ||
        el.querySelector('input[type="number"], input[inputmode="numeric"]');
      const qty = Number(input?.value ?? 0);
      const price = Number(input.getAttribute('data-price') || 0);
      if (Number.isFinite(qty) && Number.isFinite(price)) {
        total += qty * price;
      }
    });

    const totalEls = root.querySelectorAll('.quick-order-list-total');
    totalEls.forEach(totalEl => {
      totalEl.textContent = new Intl.NumberFormat(Shopify.locale, { style: "currency", currency: Shopify.currency.active }).format(total / 100);

      if (total == 0) {
        // Only hide if the paint calculator hasn't explicitly shown the button
        const hasPaintResult = document.querySelector('paint-calculator .calculator--add-to-cart-button:not(.hidden)');
        if (!hasPaintResult) {
          this.classList.add('hidden');
          if (totalEl.closest('price-list')) {
            totalEl.closest('price-list').classList.add('hidden');
          }
          if (totalEl.closest('.product-info__block-item')) {
            totalEl.closest('.product-info__block-item').querySelector('quick-order-list-submit').classList.add('hidden');
          }
        }
      } else {
        this.classList.remove('hidden');
        if (totalEl.closest('price-list')) {
          totalEl.closest('price-list').classList.remove('hidden');
        }
        document.querySelectorAll('quick-order-list-submit').forEach(btn => btn.classList.remove('hidden'));
      }
    });
  };

  onClick = () => {
    const root = this.closest('quick-order-list') || document.querySelector('quick-order-list') || document;
    const selectors = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id]')
    );
    const includeZero = this.hasAttribute('include-zero');
    const updates = {};
    for (const el of selectors) {
      const variantId = el.getAttribute('variant-id');
      const input =
        el.querySelector('.quantity-selector__input-wrapper input') ||
        el.querySelector('input[type="number"], input[inputmode="numeric"]');
      const qty = Number(input?.value ?? 0);
      if (!Number.isFinite(qty) || !variantId) continue;
      if (includeZero || qty > 0) {
        updates[variantId] = qty;
      }
    }

    if (!Object.keys(updates).length) {
      this.setAttribute('aria-disabled', 'true');
      setTimeout(() => this.removeAttribute('aria-disabled'), 150);
      return;
    }

    this.setAttribute('aria-busy', 'true');

    const quickOrderList = root.querySelector('quick-order-list') || root.closest('quick-order-list');
    const eventTarget = quickOrderList || root || document;
    eventTarget.dispatchEvent(
      new CustomEvent('quick-order-list:update', {
        bubbles: true,
        composed: true,
        detail: { updates },
      })
    );

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.show();
      cartDrawer.classList.add('open');
    }

    setTimeout(() => this.removeAttribute('aria-busy'), 800);

    this._quantityInputs = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id] input[type="number"], quick-order-list-quantity-selector[variant-id] input[inputmode="numeric"]')
    );
    if (cartDrawer) {
      setTimeout(() => {
        this._quantityInputs.forEach(input => {
          input.value = 0;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }, 2000);
    }
  };

  _onVariantChange = () => {
    this._unbindQuantityChange();
    this._bindQuantityChange();
    this._updateTotalPrice();
  };
}

if (!customElements.get('quick-order-list-submit')) {
  customElements.define('quick-order-list-submit', QuickOrderListSubmit);
}


/* -------------------------------------------
 * <paint-calculator>
 * ----------------------------------------- */

class PaintCalculator extends HTMLElement {
  constructor() {
    super();
    this.unitSystem = 'metric';
    this.coverage_value = Number(this.dataset.coverage);
    const variantPicker = this.closest('variant-picker');
    if (variantPicker) {
      this.paintOptions = Array.from(
        variantPicker.querySelectorAll('.quick-order-list__table input[type="radio"]:not(:disabled)')
      );
    } else {
      this.paintOptions = [];
      console.warn('PaintCalculator: No parent variant-picker found.');
    }
    this.coverage = {
      metric: this.coverage_value,
      imperial: 400
    };
  }

  connectedCallback() {
    this.widthInput = document.getElementById('room_width');
    this.lengthInput = document.getElementById('room_length');
    this.heightInput = document.getElementById('room_height');
    this.resultEl = this.querySelector('.result');
    this.totalCoverageEl = this.querySelector('.entered-covarage');

    if (!this.widthInput || !this.lengthInput || !this.heightInput || !this.resultEl) {
      console.warn('PaintCalculator: Missing required input or result elements.');
      return;
    }

    const debounceDelay = 150;
    const scheduleUpdate = () => {
      clearTimeout(this._updateTimer);
      this._updateTimer = setTimeout(() => this.updateResult(), debounceDelay);
    };

    this.widthInput.addEventListener('input', scheduleUpdate);
    this.lengthInput.addEventListener('input', scheduleUpdate);
    this.heightInput.addEventListener('input', scheduleUpdate);
    this.widthInput.addEventListener('change', scheduleUpdate);
    this.lengthInput.addEventListener('change', scheduleUpdate);
    this.heightInput.addEventListener('change', scheduleUpdate);

    // Run initial state to set correct prompt/button visibility
    this.updateResult();
  }

  toggleUnits() {
    this.unitSystem = this.unitSystem === 'metric' ? 'imperial' : 'metric';
    this.updateResult();
  }

  loadPaintVariant(paintNeeded) {
    let optionsArr = this.paintOptions.map(option => {
      let size = parseFloat(option.getAttribute('data-paint-size')) || 0;
      let unit = option.getAttribute('data-paint-unit') || 'litre';
      let price = parseFloat(option.getAttribute('data-paint-price')) || 0;
      return { option, size, unit, price };
    });

    optionsArr = optionsArr.filter(o =>
      (this.unitSystem === 'metric' && o.unit === 'litre') ||
      (this.unitSystem === 'imperial' && o.unit === 'gallons')
    );

    optionsArr.sort((a, b) => b.size - a.size);

    // Reset all quantities to 0
    optionsArr.forEach(o => {
      const row = o.option.closest('tr');
      if (!row) return;
      const input = row.querySelector('.quantity-selector__input');
      if (!input) return;
      input.value = 0;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (paintNeeded === 0) {
      this.querySelector('.calculator--add-to-cart-button')?.classList.add('hidden');
      const submitBtn = this.querySelector('quick-order-list-submit');
      if (submitBtn) submitBtn.classList.add('hidden');
      return;
    }

    if (optionsArr.length === 0) return;

    let bestCombo = [];
    let bestPrice = Infinity;
    let bestPaint = Infinity;
    let bestWaste = Infinity;

    // Find the largest container available
    const largestOption = optionsArr[0]; // already sorted by size descending

    if (largestOption) {
      // Calculate minimum containers needed to cover paintNeeded
      const minContainers = Math.ceil(paintNeeded / largestOption.size);
      // Try from minimum up to minimum + 3 to find the optimal combo
      const maxContainers = minContainers + 3;

      const singleContainerSolution = optionsArr.find(o => o.size >= paintNeeded);
      if (singleContainerSolution) {
        bestCombo = [singleContainerSolution];
        bestPrice = singleContainerSolution.price;
        bestPaint = singleContainerSolution.size;
        bestWaste = bestPaint - paintNeeded;
      }

      const tryAllCombinations = (maxC) => {
        const generateCombos = (currentCombo, startIndex, remainingSlots) => {
          if (remainingSlots === 0 || startIndex >= optionsArr.length) {
            if (currentCombo.length > 0) {
              const paint = currentCombo.reduce((sum, o) => sum + o.size, 0);
              const price = currentCombo.reduce((sum, o) => sum + o.price, 0);
              const waste = paint - paintNeeded;

              if (paint >= paintNeeded && waste >= 0 && price > 0) {
                const isBetter =
                  price < bestPrice ||
                  (price === bestPrice && paint > bestPaint) ||
                  (price === bestPrice && paint === bestPaint && waste < bestWaste);

                if (isBetter) {
                  bestCombo = [...currentCombo];
                  bestPrice = price;
                  bestPaint = paint;
                  bestWaste = waste;
                }
              }
            }
            return;
          }

          for (let i = startIndex; i < optionsArr.length; i++) {
            currentCombo.push(optionsArr[i]);
            generateCombos(currentCombo, i, remainingSlots - 1);
            currentCombo.pop();
          }
        };

        generateCombos([], 0, maxC);
      };

      for (let c = 1; c <= maxContainers; c++) {
        tryAllCombinations(c);
      }
    }

    // Fallback: if no combo found, stack largest container until covered
    if (bestCombo.length === 0 && largestOption) {
      const count = Math.ceil(paintNeeded / largestOption.size);
      bestCombo = Array(count).fill(largestOption);
      bestPrice = largestOption.price * count;
      bestPaint = largestOption.size * count;
      bestWaste = bestPaint - paintNeeded;
    }

    // Apply the best combination quantities
    const counts = new Map();
    bestCombo.forEach(o => {
      const optEl = o.option;
      counts.set(optEl, (counts.get(optEl) || 0) + 1);
    });

    counts.forEach((count, optEl) => {
      const row = optEl.closest('tr');
      if (!row) return;
      const input = row.querySelector('.quantity-selector__input');
      if (!input) return;
      const current = Number(input.value) || 0;
      input.value = current + count;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (bestPrice > 0 && bestPrice !== Infinity) {
      this.querySelector('.calculator--add-to-cart-button')?.classList.remove('hidden');
      const submitBtn = this.querySelector('quick-order-list-submit');
      if (submitBtn) submitBtn.classList.remove('hidden');
    } else {
      this.querySelector('.calculator--add-to-cart-button')?.classList.add('hidden');
      const submitBtn = this.querySelector('quick-order-list-submit');
      if (submitBtn) submitBtn.classList.add('hidden');
    }
  }

  updateResult() {
    const width = parseFloat(this.widthInput.value) || 0;
    const length = parseFloat(this.lengthInput.value) || 0;
    const height = parseFloat(this.heightInput.value) || 0;

    const promptEl = this.querySelector('.calculator--prompt');
    const estimateEl = this.querySelector('.calculator--estimate');
    const coverageEl = this.querySelector('.calculator--coverage');

    // Fix minus buttons — enable/disable based on current value
    [this.widthInput, this.lengthInput, this.heightInput].forEach(input => {
      const minusBtn = input.closest('quantity-selector')?.querySelector('.quantity-selector__button:first-child');
      if (minusBtn) {
        const val = parseFloat(input.value) || 0;
        const min = parseFloat(input.min) || 0;
        minusBtn.style.opacity = val <= min ? '0.5' : '1';
        minusBtn.disabled = val <= min;
      }
    });

    // No dimensions entered yet
    if (width === 0 && length === 0) {
      if (promptEl) promptEl.style.display = '';
      if (estimateEl) estimateEl.style.display = 'none';
      if (coverageEl) coverageEl.style.display = 'none';
      this.querySelector('.calculator--add-to-cart-button')?.classList.add('hidden');
      const submitBtn = this.querySelector('quick-order-list-submit');
      if (submitBtn) submitBtn.classList.add('hidden');
      return;
    }

    const area = (2 * width * height) + (2 * length * height);
    const coats = 2;
    const totalArea = area * coats;

    let paintNeeded;
    let unitLabel;

    if (this.unitSystem === 'metric') {
      paintNeeded = totalArea === 0 ? 0 : Math.ceil(totalArea / this.coverage.metric);
      unitLabel = 'litres';
    } else {
      paintNeeded = totalArea === 0 ? 0 : Math.ceil((totalArea / this.coverage.imperial) * 10) / 10;
      unitLabel = 'gallons';
    }

    if (promptEl) promptEl.style.display = 'none';
    if (estimateEl) estimateEl.style.display = '';
    if (coverageEl) coverageEl.style.display = '';

    this.resultEl.textContent = `${paintNeeded} ${unitLabel}`;
    this.totalCoverageEl.textContent = `${area.toFixed(2)} ${this.unitSystem === 'metric' ? 'm²' : 'ft²'}`;

    if (paintNeeded >= 0) {
      setTimeout(() => this.loadPaintVariant(paintNeeded), 1000);
    }
  }
}

customElements.define('paint-calculator', PaintCalculator);


/* -------------------------------------------
 * <accordion-disclosure-drawer>
 * ----------------------------------------- */

var AccordionDisclosureDrawer = class extends AccordionDisclosure {
  static get observedAttributes() {
    return ['disabled'];
  }

  constructor() {
    super();
    this._onSummaryClick = this._onSummaryClick.bind(this);
    this._onSummaryKeydown = this._onSummaryKeydown.bind(this);
    this._onContentChange = this._onContentChange.bind(this);
    this._onScrollDown = this._onScrollDown.bind(this);
  }

  connectedCallback() {
    super.connectedCallback && super.connectedCallback();

    this._updateSelectedOption();

    const content = this.querySelector('.accordion__content');
    if (content) {
      content.addEventListener('change', this._onContentChange);
    }

    const selectedOptionEl = this.querySelector('selected-option');
    if (selectedOptionEl) {
      selectedOptionEl.addEventListener('click', () => {
        if (!this.hasAttribute('disabled')) {
          this.open && this.open();
        }
      });
    }

    this._summary = this.querySelector('summary, .accordion__summary');
    if (this._summary) {
      this._summary.addEventListener('click', this._onSummaryClick, true);
      this._summary.addEventListener('keydown', this._onSummaryKeydown, true);
    }

    if (this.hasAttribute('disabled')) this._applyDisabledState();
    if (this.classList.contains('accordion--finish')) {
      document.addEventListener('dialog:body-scroll', this._onScrollDown);
    }
  }

  disconnectedCallback() {
    const content = this.querySelector('.accordion__content');
    if (content) {
      content.removeEventListener('change', this._onContentChange);
    }

    const selectedOptionEl = this.querySelector('selected-option');
    if (selectedOptionEl) {
      selectedOptionEl.removeEventListener('click', () => { });
    }

    if (this._summary) {
      this._summary.removeEventListener('click', this._onSummaryClick, true);
      this._summary.removeEventListener('keydown', this._onSummaryKeydown, true);
    }

    super.disconnectedCallback && super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (this.hasAttribute('disabled')) {
        this._applyDisabledState();
      } else {
        this._removeDisabledState();
      }
    }
  }

  _onScrollDown(e) {
    const detail = e?.detail ?? {};
    const scrollY = Number(detail.scrollY ?? detail.scrollTop ?? detail.y ?? window.pageYOffset ?? document.documentElement?.scrollTop ?? 0);
    const threshold = 5;

    if (typeof this._lastScrollY === 'undefined') this._lastScrollY = scrollY;

    const isScrollDown = scrollY - this._lastScrollY > threshold;
    this._lastScrollY = scrollY;
    if (isScrollDown && !this.hasAttribute('disabled')) {
      super.close && super.close();
    }
  }

  _onContentChange(e) {
    if (e.target.matches('input[type="radio"]')) {
      this._updateSelectedOption();
    }
  }

  _onSummaryClick(e) {
    if (this.hasAttribute('disabled')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.removeAttribute('open');
      this.setAttribute('aria-expanded', 'false');
    }
  }

  _onSummaryKeydown(e) {
    if (!this.hasAttribute('disabled')) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }

  _applyDisabledState() {
    this.setAttribute('aria-disabled', 'true');
    this.removeAttribute('open');
    this.setAttribute('aria-expanded', 'false');
    if (this._summary) {
      this._previousTabIndex = this._summary.getAttribute('tabindex');
      this._summary.setAttribute('tabindex', '-1');
      this._summary.classList.add('is-disabled');
    }
  }

  _removeDisabledState() {
    this.removeAttribute('aria-disabled');
    if (this._summary) {
      if (this._previousTabIndex !== null && this._previousTabIndex !== undefined) {
        this._summary.setAttribute('tabindex', this._previousTabIndex);
      } else {
        this._summary.removeAttribute('tabindex');
      }
      this._summary.classList.remove('is-disabled');
    }
  }

  open() {
    if (this.hasAttribute('disabled')) return;
    return super.open && super.open();
  }

  close() {
    if (this.hasAttribute('disabled')) return;
    return super.close && super.close();
  }

  _updateSelectedOption() {
    const content = this.querySelector('.accordion__content');
    const selectedOptionEl = this.querySelector('selected-option span');
    if (!content || !selectedOptionEl) return;

    const checkedInput = content.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      const label = content.querySelector(`label[for="${checkedInput.id}"] span.heading`);
      if (label) {
        selectedOptionEl.innerHTML = '';
        selectedOptionEl.appendChild(label.cloneNode(true));
      } else {
        selectedOptionEl.textContent = checkedInput.value;
      }
      selectedOptionEl.setAttribute('aria-hidden', 'false');
      selectedOptionEl.closest('.checkbox-control').querySelector('input[type="checkbox"]').checked = true;
    } else {
      selectedOptionEl.textContent = 'Select finish';
      selectedOptionEl.setAttribute('aria-hidden', 'true');
    }
  }
};

if (!window.customElements.get("accordion-disclosure-drawer")) {
  window.customElements.define("accordion-disclosure-drawer", AccordionDisclosureDrawer);
}


document.addEventListener('DOMContentLoaded', () => {
  const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams.has('variant')) {
    document.querySelectorAll('.variant-picker__option-values input:checked').forEach(d => {
      d.checked = false;
    });
  }
});


/* -------------------------------------------
 * <variant-information>
 * ----------------------------------------- */

class VariantInformation extends HTMLElement {
  constructor() {
    super();
    this._onVariantChange = this._onVariantChange.bind(this);
  }

  connectedCallback() {
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  _onVariantChange(e) {
    const id = e.detail.variant.id;
    if (!id) return;
    this._updateVisibility(id);
  }

  _updateVisibility(currentId) {
    document.querySelectorAll('[data-variant-info-id]').forEach((el) => {
      if (Number(el.dataset.variantInfoId) === currentId) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });
  }
}

if (!customElements.get('variant-information')) {
  customElements.define('variant-information', VariantInformation);
}


/* -------------------------------------------
 * <header-bar-carousel>
 * ----------------------------------------- */

import { animate as animate11 } from "vendor";

var HeaderBarCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return {
      leaveControls: () => animate11(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, { duration: 0.25, easing: [0.55, 0.055, 0.675, 0.19] }),
      enterControls: () => animate11(toSlide, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0px)"] }, { duration: 0.4, easing: [0.215, 0.61, 0.355, 1] })
    };
  }
};
if (!window.customElements.get("header-bar-carousel")) {
  window.customElements.define("header-bar-carousel", HeaderBarCarousel);
}


/* -------------------------------------------
 * <drawer-color-catalog>
 * ----------------------------------------- */

class DrawerColorCatalog extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._isLoading = false;
    this._loadedPages = new Set();
  }
  connectedCallback() {
    document.addEventListener('dialog:body-scroll', this._onScroll);
  }
  disconnectedCallback() {
    document.removeEventListener('dialog:body-scroll', this._onScroll);
  }
  _onScroll() {
    if (this._isLoading) return;
    this._loadMoreColors();
  }
  async _loadMoreColors() {
    const nextPageLink = this.querySelector('nav a');
    const nextPageUrl = nextPageLink?.href;
    if (!nextPageUrl || this._loadedPages.has(nextPageUrl)) return;

    this._isLoading = true;
    try {
      const response = await fetch(nextPageUrl);
      const data = await response.text();
      this._appendColors(data);
      this._loadedPages.add(nextPageUrl);
      nextPageLink.setAttribute('data-loaded', 'true');
    } catch (error) {
      console.error('Error loading more colors:', error);
    } finally {
      this._isLoading = false;
    }
  }
  _appendColors(data) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(data, 'text/html');
    const newColors = doc.querySelectorAll('.color_entry.color_entry--drawer');
    const colorContainer = this.querySelector('.color-entries');
    const lastEntry = colorContainer.lastElementChild;
    newColors.forEach(color => {
      color.setAttribute('data-loaded', 'true');
      if (lastEntry) {
        colorContainer.insertBefore(color, lastEntry.nextSibling);
      } else {
        colorContainer.appendChild(color);
      }
    });
  }
}

customElements.define('drawer-color-catalog', DrawerColorCatalog);


/* -------------------------------------------
 * Popover variant selector
 * ----------------------------------------- */

let isUpdating = false;

document.addEventListener('click', (e) => {
  const label = e.target.closest('.popover__value-option');
  if (!label || isUpdating) return;

  const radioInput = label.querySelector('input[type="radio"]');
  if (!radioInput) return;

  isUpdating = true;

  const popover = label.closest('x-popover');
  if (!popover) {
    isUpdating = false;
    return;
  }

  const allRadios = popover.querySelectorAll('input[type="radio"]');
  allRadios.forEach(radio => {
    if (radio === radioInput) {
      radio.setAttribute('checked', 'checked');
      radio.checked = true;
    } else {
      radio.removeAttribute('checked');
      radio.checked = false;
    }
  });

  const popoverId = popover.getAttribute('id');
  const selectedValueSpan = document.querySelector(`#${popoverId}-selected-value`);
  if (selectedValueSpan) {
    const optionText = label.querySelector('span');
    if (optionText) {
      selectedValueSpan.innerHTML = optionText.innerHTML;
    }
  }

  const optionNameSpan = label.querySelector('[data-option-name]');
  const optionName = optionNameSpan?.dataset.optionName || label.textContent.split(' - ')[0].trim();

  const variantPicker = popover.closest('variant-picker');
  if (!variantPicker) {
    isUpdating = false;
    return;
  }

  const variantScript = variantPicker.querySelector('script[data-variant]');
  if (!variantScript) {
    isUpdating = false;
    return;
  }

  const handle = variantPicker.getAttribute('handle');
  const formId = variantPicker.getAttribute('form-id');

  if (!handle || !formId) {
    isUpdating = false;
    return;
  }

  fetch(`/products/${handle}.js`)
    .then(response => response.json())
    .then(product => {
      const matchingVariant = product.variants.find(v =>
        v.option1 === optionName ||
        v.option2 === optionName ||
        v.option3 === optionName
      );

      if (matchingVariant) {
        const form = document.getElementById(formId);
        if (form) {
          const hiddenIdInput = form.querySelector('input[type="hidden"][name="id"]');
          if (hiddenIdInput) {
            hiddenIdInput.value = matchingVariant.id;
          }
        }

        variantScript.textContent = JSON.stringify(matchingVariant);

        document.dispatchEvent(new CustomEvent('variant:change', {
          detail: { variant: matchingVariant }
        }));

        popover.removeAttribute('open');
        popover.style.display = 'none';

        const button = document.querySelector(`button[aria-controls="${popoverId}"]`);
        if (button) {
          button.setAttribute('aria-expanded', 'false');
        }
      }

      isUpdating = false;
    })
    .catch(error => {
      console.error('Error fetching product variants:', error);
      isUpdating = false;
    });
});